import { NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BannersConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { CasinoGameViewModel } from '@frontend/sports/types/components/banners/models';
import { DsCard } from '@frontend/ui/card';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { asapScheduler, debounceTime, distinctUntilChanged, map } from 'rxjs';

import { BannerTrackingService } from './banner-tracking.service';

@Component({
    selector: 'ms-banner-games-multi-row',
    templateUrl: './banner-games-multi-row.component.html',
    styleUrl: './banner-games-multi-row.component.scss',
    imports: [NgOptimizedImage, IconCustomComponent, DsCard],
})
export class BannerGamesMultiRowComponent implements AfterViewInit {
    private readonly GAMES_PER_ROW_CSS_VAR = '--games-per-row';
    private readonly DEFAULT_GAMES_PER_ROW = 4;

    private containerRef = viewChild<ElementRef<HTMLDivElement>>('container');
    private readonly resizeObserver = inject(ResizeObserverService);
    private readonly destroyRef = inject(DestroyRef);
    public readonly sitecore = inject(Sitecore);
    private readonly bannerConfig = inject(BannersConfig);
    private readonly trackingService = inject(BannerTrackingService);

    readonly openGame = output<{ game: CasinoGameViewModel; index: number }>();
    games = input.required<CasinoGameViewModel[]>();
    position = input.required<string>();
    isExpanded = signal(false);
    private gamesPerRow = signal(this.DEFAULT_GAMES_PER_ROW);

    filteredGames = computed<CasinoGameViewModel[]>(() => {
        if (this.isExpanded() || !this.isShowMoreVisible()) {
            return this.games();
        }

        const gamesCount = this.gamesPerRow() * this.bannerConfig.games.noOfRows;

        return this.games().slice(0, gamesCount);
    });

    isShowMoreVisible = computed(() => this.gamesPerRow() * this.bannerConfig.games.noOfRows < this.games().length);

    toggleExpand() {
        this.isExpanded.set(!this.isExpanded());
        this.trackingService.trackGymlShowMoreShowLessClick(this.position(), this.isExpanded());
    }

    ngAfterViewInit(): void {
        this.resizeObserver
            .observe(this.containerRef()!)
            .pipe(
                map((observer) => observer.contentRect.width),
                distinctUntilChanged(),
                debounceTime(0, asapScheduler),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.setGamesPerRow());

        this.setGamesPerRow();
    }

    private setGamesPerRow(): void {
        const style = getComputedStyle(this.containerRef()!.nativeElement);
        this.gamesPerRow.set(Number(style.getPropertyValue(this.GAMES_PER_ROW_CSS_VAR)));
    }
}
